import { MockarooFieldSelect } from './MockarooFieldSelect';
import { findFieldDef } from '../lib/mockaroo-fields';
import { guessFieldType } from '../lib/smart-mapping';
import type { ColumnSchema } from '../lib/api';

/**
 * Editable table of (column name × Mockaroo type × options).
 *
 * Read-only mode (when `disabled`) is used for built-in templates the
 * caller can't edit; the user can still generate from them.
 */
export function SchemaEditor({
  schema,
  onChange,
  disabled,
}: {
  schema: ColumnSchema[];
  onChange: (next: ColumnSchema[]) => void;
  disabled?: boolean;
}) {
  const updateRow = (idx: number, patch: Partial<ColumnSchema>) => {
    onChange(schema.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };
  const moveRow = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= schema.length) return;
    const next = schema.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };
  const removeRow = (idx: number) => {
    onChange(schema.filter((_, i) => i !== idx));
  };
  const addRow = () => {
    onChange([...schema, { name: `column_${schema.length + 1}`, type: 'Words', options: { min: 1, max: 2 } }]);
  };
  const resmartAll = () => {
    onChange(
      schema.map((c) => {
        const guess = guessFieldType(c.name);
        return { ...c, type: guess.type, options: guess.options ?? {} };
      }),
    );
  };

  return (
    <div className="stack">
      <div className="row">
        <strong>Schema ({schema.length} {schema.length === 1 ? 'column' : 'columns'})</strong>
        <span className="spacer" />
        {!disabled && schema.length > 0 && (
          <button type="button" onClick={resmartAll} title="Re-apply smart type suggestions to every column">
            Re-suggest types
          </button>
        )}
        {!disabled && (
          <button type="button" className="primary" onClick={addRow}>
            + Column
          </button>
        )}
      </div>

      {schema.length === 0 ? (
        <div className="banner">No columns yet. Add one above, or paste a CSV header row.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: '4ch' }}>#</th>
                <th>Column name</th>
                <th style={{ width: 220 }}>Mockaroo type</th>
                <th>Options</th>
                {!disabled && <th style={{ width: 110 }}></th>}
              </tr>
            </thead>
            <tbody>
              {schema.map((col, idx) => (
                <tr key={idx}>
                  <td className="dim">{idx + 1}</td>
                  <td>
                    <input
                      value={col.name}
                      onChange={(e) => updateRow(idx, { name: e.target.value })}
                      disabled={disabled}
                      aria-label={`Column ${idx + 1} name`}
                    />
                  </td>
                  <td>
                    {disabled ? (
                      <span>{col.type}</span>
                    ) : (
                      <MockarooFieldSelect
                        value={col.type}
                        ariaLabel={`Column ${idx + 1} type`}
                        onChange={(next) => {
                          const def = findFieldDef(next);
                          const defaults: Record<string, unknown> = {};
                          for (const opt of def?.options ?? []) {
                            if (opt.default !== undefined) defaults[opt.name] = opt.default;
                          }
                          updateRow(idx, { type: next, options: defaults });
                        }}
                      />
                    )}
                  </td>
                  <td>
                    <OptionEditor
                      type={col.type}
                      options={col.options}
                      onChange={(next) => updateRow(idx, { options: next })}
                      disabled={disabled}
                    />
                  </td>
                  {!disabled && (
                    <td>
                      <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                        <button type="button" onClick={() => moveRow(idx, -1)} title="Move up" disabled={idx === 0}>↑</button>
                        <button type="button" onClick={() => moveRow(idx, 1)} title="Move down" disabled={idx === schema.length - 1}>↓</button>
                        <button type="button" className="danger" onClick={() => removeRow(idx)} title="Remove">×</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OptionEditor({
  type,
  options,
  onChange,
  disabled,
}: {
  type: string;
  options: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
}) {
  const def = findFieldDef(type);
  if (!def?.options || def.options.length === 0) {
    return <span className="dim">—</span>;
  }
  return (
    <div className="stack" style={{ gap: 4 }}>
      {def.options.map((opt) => {
        const value = options[opt.name];
        const idAttr = `opt-${type}-${opt.name}`;
        return (
          <div key={opt.name} className="row" style={{ gap: 6 }}>
            <label htmlFor={idAttr} style={{ width: 90, fontSize: '0.85em', color: 'var(--text-secondary)' }}>
              {opt.label}
            </label>
            {opt.control === 'multiline' ? (
              <textarea
                id={idAttr}
                value={String(value ?? '')}
                onChange={(e) => onChange({ ...options, [opt.name]: e.target.value })}
                disabled={disabled}
                rows={4}
                style={{ minHeight: 80 }}
              />
            ) : opt.control === 'boolean' ? (
              <input
                id={idAttr}
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e) => onChange({ ...options, [opt.name]: e.target.checked })}
                disabled={disabled}
                style={{ width: 'auto' }}
              />
            ) : (
              <input
                id={idAttr}
                type={opt.control === 'number' ? 'number' : 'text'}
                value={value == null ? '' : String(value)}
                placeholder={opt.help}
                onChange={(e) => {
                  const v = opt.control === 'number' ? Number(e.target.value) : e.target.value;
                  onChange({ ...options, [opt.name]: v });
                }}
                disabled={disabled}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

